import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { ArrowLeft, User, ShieldOff, ShieldCheck, KeyRound, MessageSquarePlus, UserCog, ExternalLink, Copy } from "lucide-react";

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [showSuspend, setShowSuspend] = useState(false);

  const load = async () => {
    try { const { data } = await adminApi.get(`/clients/${id}`); setD(data); }
    catch { toast.error("Erreur"); nav("/admin/clients"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!d) return <div className="text-slate-400">Chargement…</div>;
  const u = d.user;

  const doSuspend = async () => {
    try { await adminApi.post(`/clients/${id}/suspend`, { reason: suspendReason, confirm_email: confirmEmail });
      toast.success("Client suspendu"); setShowSuspend(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doReactivate = async () => {
    try { await adminApi.post(`/clients/${id}/reactivate`); toast.success("Réactivé"); load(); }
    catch { toast.error("Erreur"); }
  };
  const doNote = async () => {
    if (!note.trim()) return;
    try { await adminApi.post(`/clients/${id}/notes`, { text: note }); toast.success("Note ajoutée"); setNote(""); load(); }
    catch { toast.error("Erreur"); }
  };
  const doResetLink = async () => {
    try { const { data } = await adminApi.post(`/clients/${id}/reset-password-link`);
      navigator.clipboard.writeText(data.reset_link_token);
      toast.success("Token de reset copié — TTL 24h");
    } catch { toast.error("Erreur"); }
  };
  const doImpersonate = async () => {
    if (!window.confirm("Impersonation lecture seule 30 min. Confirmer ?")) return;
    try { const { data } = await adminApi.post(`/clients/${id}/impersonate`);
      // Sauvegarder token client dans localStorage sous une clé dédiée + ouvrir /app en lecture seule
      sessionStorage.setItem("empco_impersonation_token", data.token);
      sessionStorage.setItem("empco_impersonation_email", data.user.email);
      // Naviguer vers /app avec bandeau rouge (voir AppShell modifs)
      window.open(`/app?impersonate=${encodeURIComponent(data.user.email)}`, "_blank");
      toast.success("Session impersonation ouverte dans un nouvel onglet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-client-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/admin/clients" className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><User className="w-6 h-6 text-emerald-400" /> {u.email}</h1>
        {u.disabled
          ? <span className="px-3 py-1 rounded-full text-xs bg-rose-500/20 text-rose-300 font-mono">SUSPENDU — {u.disabled_reason || ""}</span>
          : <span className="px-3 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-300 font-mono">ACTIF</span>}
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {[
          { l: "Scans", v: d.kpi.scans_total },
          { l: "Scans payés", v: d.kpi.scans_paid },
          { l: "CA client", v: `${d.kpi.revenue_eur} €` },
          { l: "MRR", v: `${d.kpi.mrr_eur} €` },
        ].map(k => (
          <div key={k.l} className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
            <p className="text-[11px] uppercase tracking-widest text-slate-500">{k.l}</p>
            <p className="text-2xl font-semibold text-white mt-2">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold mb-3">Identité</h3>
          <dl className="grid grid-cols-2 gap-3 text-xs">
            <div><dt className="text-slate-500">Prénom / Nom</dt><dd>{u.first_name} {u.last_name}</dd></div>
            <div><dt className="text-slate-500">Entreprise</dt><dd>{u.company || "—"}</dd></div>
            <div><dt className="text-slate-500">Code TVA</dt><dd className="font-mono">{u.vat_code || "—"}</dd></div>
            <div><dt className="text-slate-500">Plan</dt><dd>{u.plan}</dd></div>
            <div><dt className="text-slate-500">Quota</dt><dd>{u.quota_used}/{u.quota_max}</dd></div>
            <div><dt className="text-slate-500">CGV acceptée</dt><dd className="font-mono">v{u.cgv_accepted_version || "?"} · {u.cgv_accepted_at?.slice(0,10) || "—"}</dd></div>
            <div><dt className="text-slate-500">IP CGV</dt><dd className="font-mono">{u.cgv_accepted_ip || "—"}</dd></div>
            <div><dt className="text-slate-500">Inscrit</dt><dd className="font-mono">{u.created_at?.slice(0,10)}</dd></div>
          </dl>
        </div>
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Actions admin</h3>
          <div className="space-y-2">
            {u.disabled
              ? <button onClick={doReactivate} data-testid="client-reactivate-btn"
                  className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                  <ShieldCheck className="w-3.5 h-3.5" /> Réactiver
                </button>
              : <button onClick={() => setShowSuspend(true)} data-testid="client-suspend-btn"
                  className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-md bg-rose-600/20 border border-rose-500/40 text-rose-300 text-xs">
                  <ShieldOff className="w-3.5 h-3.5" /> Suspendre
                </button>}
            <button onClick={doResetLink} data-testid="client-reset-btn"
              className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <KeyRound className="w-3.5 h-3.5" /> Lien reset password
            </button>
            <button onClick={doImpersonate} data-testid="client-impersonate-btn"
              className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-600/20 border border-amber-500/40 text-amber-300 text-xs">
              <UserCog className="w-3.5 h-3.5" /> Impersonation (lecture seule 30min)
            </button>
          </div>
        </div>
      </div>

      {/* Notes internes */}
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Notes internes</h3>
        <div className="flex gap-2 mb-3">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Ajouter une note…" data-testid="client-note-input"
            className="flex-1 bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <button onClick={doNote} data-testid="client-note-add"
            className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs">Ajouter</button>
        </div>
        <ul className="space-y-2 max-h-40 overflow-auto">
          {d.notes.map(n => (
            <li key={n.id} className="text-xs border border-slate-800 rounded-md p-3">
              <p className="text-slate-300">{n.text}</p>
              <p className="text-[10px] text-slate-500 mt-1 font-mono">{n.author} · {n.created_at?.slice(0,16).replace("T"," ")}</p>
            </li>
          ))}
          {d.notes.length === 0 && <li className="text-xs text-slate-500 text-center py-4">Aucune note</li>}
        </ul>
      </div>

      {/* Historique scans + factures */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Derniers scans</h3>
          <div className="overflow-auto max-h-72 text-xs">
            {d.scans.slice(0, 20).map(s => (
              <div key={s.id} className="flex justify-between border-b border-slate-800 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-slate-200">{s.url}</p>
                  <p className="text-[10px] text-slate-500 font-mono">{s.source || "web"} · {s.scanned_at?.slice(0,16).replace("T"," ")}</p>
                </div>
                <div className="ml-2 text-right">
                  <p className="font-mono text-emerald-400">{s.compliance_score || 0}/100</p>
                  <p className="text-[10px] text-slate-500">R{s.red_count} O{s.orange_count}</p>
                </div>
              </div>
            ))}
            {d.scans.length === 0 && <p className="text-slate-500 text-center py-4">Aucun scan</p>}
          </div>
        </div>
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Factures</h3>
          <div className="overflow-auto max-h-72 text-xs">
            {d.orders.slice(0, 20).map(o => (
              <div key={o.id} className="flex justify-between border-b border-slate-800 py-2">
                <div>
                  <p className="text-slate-200 font-mono">{o.invoice_number}</p>
                  <p className="text-[10px] text-slate-500">{o.label}</p>
                </div>
                <p className="font-mono text-emerald-400">{o.total_eur} €</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Suspend modal */}
      {showSuspend && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowSuspend(false)}>
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="suspend-modal">
            <h3 className="text-lg font-semibold mb-3 text-rose-400">Suspendre {u.email}</h3>
            <input value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Raison" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs mb-3" data-testid="suspend-reason" />
            <p className="text-xs text-slate-400 mb-2">Retapez l'e-mail pour confirmer :</p>
            <input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} placeholder={u.email} className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs mb-4 font-mono" data-testid="suspend-confirm-email" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSuspend(false)} className="px-4 py-2 rounded-md text-xs text-slate-400 hover:text-white">Annuler</button>
              <button onClick={doSuspend} disabled={!suspendReason || confirmEmail !== u.email} data-testid="suspend-confirm-btn"
                className="px-4 py-2 rounded-md bg-rose-600 disabled:opacity-40 text-white text-xs">Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
